import Link from "next/link";
import { InferGetStaticPropsType } from "next";
import React, { VFC } from "react";

import Layout from "components/layout";
import { ArticleCard, Btn } from "components/utils";

import { admin_db } from "lib/firebase-admin";
import { useUser } from "hooks/useUser";
import { convertDate, shuffle } from "lib/utils";
import { signin } from "lib/signin";
import { Article, IndexArticle } from "lib/type";
import { Timestamp } from "firebase-admin/firestore";

import { FaRegEdit } from "react-icons/fa";
import { FiClock } from "react-icons/fi";

let tagCloud = [""];

let articles: IndexArticle[] = [
  {
    author: null,
    articleID: "",
    title: "",
    thumb: "☺",
    color: "#ff0000",
    createdAt: "",
  },
];

export const getStaticProps = async () => {
  const tagDocs = await admin_db
    .collection("tags")
    .limit(30)
    .get()
    .catch((err) => {
      console.log(err);
      return null;
    });
  if (!tagDocs) return { props: { tagCloud, articles } };
  tagCloud = shuffle(tagDocs.docs.map((doc) => doc.data().name));

  const articleDocs = await admin_db
    .collectionGroup("publicArticles")
    .orderBy("createdAt", "desc")
    .limit(20)
    .get()
    .catch((err) => {
      console.log(err);
      return null;
    });

  if (!articleDocs || articleDocs.empty)
    return { props: { tagCloud, articles } };
  articles = await Promise.all(
    articleDocs.docs.map(async (articleDoc) => {
      const article = articleDoc.data() as Article<Timestamp>;
      const { authorUid, thumb, color, title, createdAt, tags, likeCount } =
        article;
      const authorDoc = await admin_db
        .collection("users")
        .doc(authorUid as string)
        .get();
      const author = await JSON.parse(JSON.stringify(authorDoc.data()));
      const createDate = convertDate(createdAt);
      return {
        articleID: articleDoc.id,
        thumb: thumb,
        color: color,
        title: title,
        author: author,
        createdAt: createDate,
        tags: tags,
        likeCount: likeCount,
      };
    })
  );

  return { props: { tagCloud, articles }, revalidate: 600 };
};

type IndexStaticProps = InferGetStaticPropsType<typeof getStaticProps>;

const Home: VFC<IndexStaticProps> = ({ tagCloud, articles }) => {
  const { user, isAuthChecking } = useUser();

  const tagList = tagCloud.map((tag, index) => {
    return (
      <li key={index} className="leading-none">
        <Link href={`/tags/${tag}/`} prefetch={false}>
          <a className="text-link dark:text-linkDark whitespace-nowrap underline">
            #{tag}
          </a>
        </Link>
      </li>
    );
  });

  const newArticles = articles
    ?.map((article) => {
      if (!article.articleID) return;
      return <ArticleCard key={article.articleID} article={article} />;
    })
    .filter((article) => article);

  return (
    <Layout>
      <div className="container lg:px-0">
        {tagList.length > 1 && (
          <nav className="overflow-x-auto p-4 md:mb-8 md:overflow-hidden md:px-0 md:pt-0">
            <ul className="flex gap-4 md:flex-wrap md:gap-y-2">{tagList}</ul>
          </nav>
        )}
        <h1 className="mx-auto mb-6 table text-base font-normal">
          <span className="text-2xl font-bold">
            {process.env.NEXT_PUBLIC_SITE_NAME}
          </span>
          は音楽に関する、知識や経験をシェアするサービスです。
        </h1>
        {isAuthChecking && <div className="h-20"></div>}
        {!isAuthChecking && user && (
          <Link href="/post">
            <a className="mx-auto mb-8 block w-[fit-content]">
              <Btn Icon={FaRegEdit}>記事を書く</Btn>
            </a>
          </Link>
        )}
        {!isAuthChecking && !user && (
          <button onClick={signin} className="mx-auto mb-8">
            <Btn Icon={FaRegEdit}>サインインして記事を書く</Btn>
          </button>
        )}
      </div>
      <section className="container px-0">
        <h2 className="mb-4 flex items-center px-4 text-2xl lg:px-0">
          <FiClock className="mr-1" />
          新しい記事
        </h2>
        {newArticles.length > 0 ? (
          <ul className="space-y-2 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
            {newArticles}
          </ul>
        ) : (
          <p className="mx-4 px-4">まだ記事はありません。</p>
        )}
      </section>
    </Layout>
  );
};

export default Home;
